import React, { useState, useCallback } from "react";
import { DayPicker, DateRange } from "react-day-picker";
import moment from "moment-timezone";
import { ptBR } from "react-day-picker/locale";
import { Button } from "../Button";
import { Tabs } from "../Tabs";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import "./styles.css";

// ========== Interfaces ==========

/**
 * Range de datas selecionadas
 */
export interface DateRangeValue {
  /** Data inicial do período */
  startDate: Date | null;
  /** Data final do período */
  endDate: Date | null;
}

/**
 * Props do componente DateRangePicker
 */
export interface DateRangePickerProps {
  /** Valor controlado (opcional) */
  value?: DateRangeValue;
  /** Callback quando o range é alterado e "Aplicar" é clicado */
  onChange: (range: DateRangeValue) => void;
  /** Data mínima selecionável (opcional) */
  minDate?: Date;
  /** Data máxima selecionável (opcional) */
  maxDate?: Date;
  /** Se o componente está desabilitado */
  disabled?: boolean;
  /** Classes CSS adicionais */
  className?: string;
  /** Callback quando "Limpar" é clicado (opcional) */
  onClear?: () => void;
  /** Se deve fechar ao clicar "Aplicar" (útil em modais) */
  closeOnApply?: boolean;
}

// ========== Subcomponente: Footer ==========

interface FooterProps {
  onClear: () => void;
  onApply: () => void;
  disabled?: boolean;
}

const DateRangePickerFooter: React.FC<FooterProps> = ({ onClear, onApply, disabled }) => {
  return (
    <div className="flex justify-between items-center mt-6 pt-4 border-t border-base-input gap-4">
      <Button color="secondary" onClick={onClear} disabled={disabled} className="flex-1">
        Limpar
      </Button>
      <Button color="primary" onClick={onApply} disabled={disabled} className="flex-[2]">
        Aplicar
      </Button>
    </div>
  );
};

// ========== Componente Principal ==========

/**
 * Componente de seleção de período (range de datas)
 *
 * @example
 * ```tsx
 * <DateRangePicker
 *   value={dateRange}
 *   onChange={setDateRange}
 *   minDate={new Date('2024-01-01')}
 * />
 * ```
 */
const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  className = "",
  onClear,
  closeOnApply = false,
}) => {
  // State para controlar tab ativa (período ou mês)
  const [activeTab, setActiveTab] = useState<"period" | "month">("period");

  // State interno temporário (enquanto usuário está selecionando)
  const [tempRange, setTempRange] = useState<DateRange | undefined>(() => {
    if (value?.startDate || value?.endDate) {
      return {
        from: value.startDate || undefined,
        to: value.endDate || undefined,
      };
    }
    return undefined;
  });

  // State para controlar quais meses mostrar
  const [displayMonth, setDisplayMonth] = useState<Date>(value?.startDate || new Date());

  // State para controlar ano selecionado na aba "mês"
  const [selectedYear, setSelectedYear] = useState<number>(() => {
    return value?.startDate ? moment(value.startDate).year() : moment().year();
  });

  // Handler quando usuário seleciona dias no calendário
  const handleRangeSelect = useCallback((range: DateRange | undefined) => {
    setTempRange(range);
  }, []);

  // Handler do botão "Limpar"
  const handleClear = useCallback(() => {
    setTempRange(undefined);
    onClear?.();
    onChange({ startDate: null, endDate: null });
  }, [onChange, onClear]);

  // Handler do botão "Aplicar"
  const handleApply = useCallback(() => {
    onChange({
      startDate: tempRange?.from || null,
      endDate: tempRange?.to || null,
    });
  }, [tempRange, onChange]);

  // Formatação do título do mês (texto simples)
  const formatMonthTitle = (date: Date) => {
    const formatted = moment(date).locale("pt-br").format("MMMM YYYY");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  // Formatação dos dias da semana em maiúsculas
  const formatWeekdayName = (date: Date): string => {
    return moment(date).locale("pt-br").format("ddd").toUpperCase();
  };

  /**
   * Handler para navegar para o ano anterior.
   * Limpa tempRange ao trocar ano (simplificação aprovada).
   */
  const handlePreviousYear = useCallback(() => {
    setSelectedYear((prev) => prev - 1);
    setTempRange(undefined); // Limpar seleção ao trocar ano (UX intuitiva)
  }, []);

  /**
   * Handler para navegar para o próximo ano.
   * Limpa tempRange ao trocar ano (simplificação aprovada).
   */
  const handleNextYear = useCallback(() => {
    setSelectedYear((prev) => prev + 1);
    setTempRange(undefined); // Limpar seleção ao trocar ano (UX intuitiva)
  }, []);

  /**
   * Deriva o mês selecionado a partir de tempRange.
   * Retorna índice do mês (0-11) se tempRange representa um mês completo do selectedYear,
   * ou 12 se representa o ano completo, ou null se não representa mês/ano completo.
   */
  const getSelectedMonth = useCallback((): number | null => {
    if (!tempRange?.from || !tempRange?.to) return null;

    const startMoment = moment(tempRange.from);
    const endMoment = moment(tempRange.to);

    // Verifica se é o ano completo (1º Jan a 31 Dez)
    const isFullYear =
      startMoment.isSame(moment().year(selectedYear).startOf("year"), "day") &&
      endMoment.isSame(moment().year(selectedYear).endOf("year"), "day");

    if (isFullYear) return 12; // Valor especial para "Todos os meses"

    // Verifica se é um mês completo
    const startYear = startMoment.year();
    const endYear = endMoment.year();
    const startMonth = startMoment.month();
    const endMonth = endMoment.month();

    const isFullMonth =
      startYear === selectedYear &&
      endYear === selectedYear &&
      startMonth === endMonth &&
      startMoment.isSame(moment().year(selectedYear).month(startMonth).startOf("month"), "day") &&
      endMoment.isSame(moment().year(selectedYear).month(startMonth).endOf("month"), "day");

    return isFullMonth ? startMonth : null;
  }, [tempRange, selectedYear]);

  /**
   * Verifica se um mês deve estar desabilitado baseado em minDate/maxDate.
   * @param monthIndex - Índice do mês (0 = Janeiro, 11 = Dezembro)
   */
  const isMonthDisabled = useCallback(
    (monthIndex: number): boolean => {
      if (!minDate && !maxDate) return false;

      const monthStart = moment().year(selectedYear).month(monthIndex).startOf("month");
      const monthEnd = moment().year(selectedYear).month(monthIndex).endOf("month");

      // Mês completamente antes de minDate
      if (minDate && monthEnd.isBefore(minDate, "day")) return true;

      // Mês completamente depois de maxDate
      if (maxDate && monthStart.isAfter(maxDate, "day")) return true;

      return false;
    },
    [selectedYear, minDate, maxDate],
  );

  /**
   * Handler para seleção de mês individual.
   * Calcula o primeiro e último dia do mês selecionado e atualiza tempRange.
   * @param monthIndex - Índice do mês (0 = Janeiro, 11 = Dezembro)
   */
  const handleMonthSelect = useCallback(
    (monthIndex: number) => {
      // Primeiro dia do mês
      const firstDay = moment().year(selectedYear).month(monthIndex).startOf("month").toDate();

      // Último dia do mês (moment lida automaticamente com 28, 29, 30, 31 dias)
      const lastDay = moment().year(selectedYear).month(monthIndex).endOf("month").toDate();

      // Atualiza tempRange (selectedMonth será derivado automaticamente via getSelectedMonth)
      setTempRange({ from: firstDay, to: lastDay });
    },
    [selectedYear],
  );

  /**
   * Handler para seleção de todos os meses do ano (ano completo).
   * Calcula range de 1º Janeiro a 31 Dezembro do ano selecionado.
   */
  const handleAllMonthsSelect = useCallback(() => {
    // Primeiro dia do ano
    const firstDay = moment().year(selectedYear).startOf("year").toDate();

    // Último dia do ano
    const lastDay = moment().year(selectedYear).endOf("year").toDate();

    // Atualiza tempRange
    setTempRange({ from: firstDay, to: lastDay });
  }, [selectedYear]);

  return (
    <div className={`date-range-picker ${className}`}>
      {/* Tabs */}
      <div className="mb-6">
        <Tabs
          items={[
            {
              id: "period",
              name: "Seleção por período",
              onClick: () => setActiveTab("period"),
            },
            {
              id: "month",
              name: "Seleção por mês",
              onClick: () => setActiveTab("month"),
            },
          ]}
          currentTabId={activeTab}
          className="flex items-center"
        />
      </div>

      {/* Renderização condicional baseada na tab ativa */}
      {activeTab === "period" && (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Calendário 1 */}
          <div className="calendar-wrapper">
            <DayPicker
              mode="range"
              selected={tempRange}
              onSelect={handleRangeSelect}
              locale={ptBR}
              month={displayMonth}
              onMonthChange={setDisplayMonth}
              disabled={[
                disabled ? { before: new Date(3000, 0, 1) } : false,
                minDate ? { before: minDate } : false,
                maxDate ? { after: maxDate } : false,
              ].filter(Boolean)}
              numberOfMonths={1}
              showOutsideDays={false}
              formatters={{
                formatCaption: formatMonthTitle,
                formatWeekdayName: formatWeekdayName,
              }}
              classNames={{
                root: "rdp-root",
                months: "rdp-months",
                month: "rdp-month",
                caption: "rdp-caption",
                caption_label: "rdp-caption-label",
                nav: "rdp-nav",
                nav_button: "rdp-nav-button",
                nav_button_previous: "rdp-nav-button-previous",
                nav_button_next: "rdp-nav-button-next",
                table: "rdp-table",
                head_row: "rdp-head-row",
                head_cell: "rdp-head-cell",
                row: "rdp-row",
                cell: "rdp-cell",
                day: "rdp-day",
                day_today: "rdp-day-today",
                day_selected: "rdp-day-selected",
                day_disabled: "rdp-day-disabled",
                day_range_start: "rdp-day-range-start",
                day_range_end: "rdp-day-range-end",
                day_range_middle: "rdp-day-range-middle",
              }}
            />
          </div>

          {/* Calendário 2 (apenas desktop) */}
          <div className="calendar-wrapper calendar-right hidden lg:block">
            <DayPicker
              mode="range"
              selected={tempRange}
              onSelect={handleRangeSelect}
              locale={ptBR}
              month={moment(displayMonth).add(1, "month").toDate()}
              onMonthChange={(newMonth) => {
                // Quando usuário navega no calendário direito, ajusta displayMonth
                // para que o calendário esquerdo mostre o mês anterior ao selecionado
                setDisplayMonth(moment(newMonth).subtract(1, "month").toDate());
              }}
              disabled={[
                disabled ? { before: new Date(3000, 0, 1) } : false,
                minDate ? { before: minDate } : false,
                maxDate ? { after: maxDate } : false,
              ].filter(Boolean)}
              numberOfMonths={1}
              showOutsideDays={false}
              formatters={{
                formatCaption: formatMonthTitle,
                formatWeekdayName: formatWeekdayName,
              }}
              classNames={{
                root: "rdp-root",
                months: "rdp-months",
                month: "rdp-month",
                caption: "rdp-caption",
                caption_label: "rdp-caption-label",
                nav: "rdp-nav",
                nav_button: "rdp-nav-button",
                nav_button_previous: "rdp-nav-button-previous",
                nav_button_next: "rdp-nav-button-next",
                table: "rdp-table",
                head_row: "rdp-head-row",
                head_cell: "rdp-head-cell",
                row: "rdp-row",
                cell: "rdp-cell",
                day: "rdp-day",
                day_today: "rdp-day-today",
                day_selected: "rdp-day-selected",
                day_disabled: "rdp-day-disabled",
                day_range_start: "rdp-day-range-start",
                day_range_end: "rdp-day-range-end",
                day_range_middle: "rdp-day-range-middle",
              }}
            />
          </div>
        </div>
      )}

      {/* Seletor de Mês (renderiza quando activeTab === "month") */}
      {activeTab === "month" && (
        <div className="month-selector">
          {/* Header com navegação de ano */}
          <div className="month-selector-header">
            <button
              onClick={handlePreviousYear}
              className="year-nav-button"
              disabled={disabled}
              aria-label="Ano anterior"
            >
              <IoChevronBack size={20} />
            </button>
            <span className="year-label">{selectedYear}</span>
            <button onClick={handleNextYear} className="year-nav-button" disabled={disabled} aria-label="Próximo ano">
              <IoChevronForward size={20} />
            </button>
          </div>

          {/* Grid de meses */}
          <div className="month-selector-grid">
            {/* Botão "Todos os meses" (primeira linha, largura total) */}
            <button
              onClick={handleAllMonthsSelect}
              disabled={disabled}
              className={`month-button all-months ${getSelectedMonth() === 12 ? "selected" : ""}`}
            >
              Todos os meses
            </button>

            {/* Grid de 12 meses (Janeiro a Dezembro) */}
            {moment
              .localeData("pt-br")
              .months()
              .map((monthName, index) => (
                <button
                  key={index}
                  onClick={() => handleMonthSelect(index)}
                  disabled={disabled || isMonthDisabled(index)}
                  className={`month-button ${getSelectedMonth() === index ? "selected" : ""}`}
                >
                  {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
                </button>
              ))}
          </div>
        </div>
      )}

      <DateRangePickerFooter onClear={handleClear} onApply={handleApply} disabled={disabled} />
    </div>
  );
};

export default DateRangePicker;
